import React, { useCallback, useEffect, useRef, useState } from "react";
import Switch from "react-bootstrap-switch";
import { useTheme } from "../../hooks/theme";
import { useLegend } from "../../hooks/legend";

// Ordenadas por matiz (roda de cor), agrupando tons parecidos: roxos -> rosa ->
// laranjas -> amarelos -> verdes -> azuis -> neutro.
const COLOR_OPTIONS = [
  {
    value: "violet",
    label: "Violeta",
    swatchClass: "fixed-plugin__swatch--violet",
  },
  {
    value: "primary",
    label: "Magenta",
    swatchClass: "fixed-plugin__swatch--primary",
  },
  {
    value: "magentavivo",
    label: "Magenta vivo",
    swatchClass: "fixed-plugin__swatch--magentavivo",
  },
  {
    value: "pink",
    label: "Pink",
    swatchClass: "fixed-plugin__swatch--pink",
  },
  {
    value: "red",
    label: "Rosa",
    swatchClass: "fixed-plugin__swatch--red",
  },
  {
    value: "rosavivo",
    label: "Rosa vivo",
    swatchClass: "fixed-plugin__swatch--rosavivo",
  },
  {
    value: "orange",
    label: "Coral",
    swatchClass: "fixed-plugin__swatch--orange",
  },
  {
    value: "coralvivo",
    label: "Coral vivo",
    swatchClass: "fixed-plugin__swatch--coralvivo",
  },
  {
    value: "nstech",
    label: "Laranja",
    swatchClass: "fixed-plugin__swatch--nstech",
  },
  {
    value: "amber",
    label: "Âmbar",
    swatchClass: "fixed-plugin__swatch--amber",
  },
  {
    value: "cimed",
    label: "Amarelo",
    swatchClass: "fixed-plugin__swatch--cimed",
  },
  {
    value: "intelipost",
    label: "Verde",
    swatchClass: "fixed-plugin__swatch--intelipost",
  },
  {
    value: "emerald",
    label: "Esmeralda",
    swatchClass: "fixed-plugin__swatch--emerald",
  },
  {
    value: "green",
    label: "Turquesa",
    swatchClass: "fixed-plugin__swatch--green",
  },
  {
    value: "turquesaviva",
    label: "Turquesa viva",
    swatchClass: "fixed-plugin__swatch--turquesaviva",
  },
  {
    value: "ciano",
    label: "Ciano",
    swatchClass: "fixed-plugin__swatch--ciano",
  },
  {
    value: "blue",
    label: "Azul",
    swatchClass: "fixed-plugin__swatch--blue",
  },
  {
    value: "azulvivo",
    label: "Azul vivo",
    swatchClass: "fixed-plugin__swatch--azulvivo",
  },
  {
    value: "indigo",
    label: "Índigo",
    swatchClass: "fixed-plugin__swatch--indigo",
  },
  {
    value: "slate",
    label: "Ardósia",
    swatchClass: "fixed-plugin__swatch--slate",
  },
];

const FixedPlugin = (props) => {
  // A legenda dinâmica só existe em gráfico. Onde não há nenhum (o painel de
  // habilitação da transportadora, PO1-3106), o controle não teria efeito — e
  // controle que não faz nada é pior que controle ausente. Default true: quem
  // não passa a prop continua vendo tudo.
  const mostrarLegenda = props.mostrarLegenda !== false;
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const { darkMode, setTheme } = useTheme();
  const { legendMode, setLegend } = useLegend();

  const activeColorOption =
    COLOR_OPTIONS.find((option) => option.value === props.activeColor) ||
    COLOR_OPTIONS[1];

  const togglePanel = useCallback(() => {
    setIsOpen((current) => !current);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleActiveMode = useCallback(() => {
    setTheme(!darkMode);
  }, [darkMode, setTheme]);

  const handleLegendMode = useCallback(() => {
    setLegend(!legendMode);
  }, [legendMode, setLegend]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      className={
        isOpen
          ? "fixed-plugin fixed-plugin--modern fixed-plugin--open"
          : "fixed-plugin fixed-plugin--modern"
      }
      data-color={props.activeColor}
      ref={rootRef}
      style={{
        width: "auto",
        background: "transparent",
        borderRadius: 0,
        textAlign: "left",
        overflow: "visible",
      }}
    >
      <button
        type="button"
        className="fixed-plugin__trigger"
        onClick={togglePanel}
        aria-expanded={isOpen}
        aria-label={
          isOpen
            ? "Fechar preferencias do painel"
            : "Abrir preferencias do painel"
        }
      >
        <i
          className="tim-icons icon-settings-gear-63 fixed-plugin__trigger-icon"
          aria-hidden="true"
        />
      </button>

      <div
        className={
          isOpen
            ? "fixed-plugin__panel fixed-plugin__panel--open"
            : "fixed-plugin__panel"
        }
        role="dialog"
        aria-label="Preferencias do painel"
      >
        <div className="fixed-plugin__panel-header">
          <div>
            <p className="fixed-plugin__eyebrow">Preferencias</p>
            <h6 className="fixed-plugin__title">Personalizar painel</h6>
          </div>
          <button
            type="button"
            className="fixed-plugin__close"
            onClick={closePanel}
            aria-label="Fechar painel de preferencias"
          >
            ×
          </button>
        </div>

        <div className="fixed-plugin__section">
          <div className="fixed-plugin__section-head">
            <div>
              <span className="fixed-plugin__section-title">Cor de destaque</span>
              <span className="fixed-plugin__section-copy">
                Menu, tabs e acoes principais.
              </span>
            </div>
            <span className="fixed-plugin__active-chip">
              {activeColorOption.label}
            </span>
          </div>

          <div className="fixed-plugin__color-row">
            {COLOR_OPTIONS.map((option) => {
              const isActive = option.value === props.activeColor;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={
                    isActive
                      ? "fixed-plugin__color-option is-active"
                      : "fixed-plugin__color-option"
                  }
                  onClick={() => props.handleActiveClick(option.value)}
                  aria-label={"Usar tema " + option.label}
                  aria-pressed={isActive}
                  title={option.label}
                >
                  <span
                    className={"fixed-plugin__swatch " + option.swatchClass}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div className="fixed-plugin__section fixed-plugin__section--stacked">
          <div className="fixed-plugin__setting">
            <div className="fixed-plugin__setting-text">
              <span className="fixed-plugin__setting-title">Modo escuro</span>
              <span className="fixed-plugin__setting-copy">
                Alterna o contraste geral da interface.
              </span>
            </div>
            <div className="fixed-plugin__switch-wrap">
              <span
                className={
                  darkMode
                    ? "fixed-plugin__switch-state is-active"
                    : "fixed-plugin__switch-state"
                }
              >
                {darkMode ? "Escuro" : "Claro"}
              </span>
              <Switch
                onChange={handleActiveMode}
                value={darkMode}
                onText=""
                offText=""
              />
            </div>
          </div>

          {mostrarLegenda ? (
          <div className="fixed-plugin__setting">
            <div className="fixed-plugin__setting-text">
              <span className="fixed-plugin__setting-title">Legenda dinamica</span>
              <span className="fixed-plugin__setting-copy">
                Exibe a legenda auxiliar quando disponivel.
              </span>
            </div>
            <div className="fixed-plugin__switch-wrap">
              <span
                className={
                  legendMode
                    ? "fixed-plugin__switch-state is-active"
                    : "fixed-plugin__switch-state"
                }
              >
                {legendMode ? "Visivel" : "Oculta"}
              </span>
              <Switch
                onChange={handleLegendMode}
                value={legendMode}
                onText=""
                offText=""
              />
            </div>
          </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FixedPlugin;
